import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { callListPurchases, type AdminPurchase } from '../firebase';
import { useSort, type SortValue } from '../sorting';

type SortKey = 'at' | 'user' | 'product' | 'credits' | 'id';

// Valor comparable por columna (ver sorting.ts). Fecha → ms; resto texto/número.
function sortValue(p: AdminPurchase, key: SortKey): SortValue {
  switch (key) {
    case 'at':
      return p.at ?? null;
    case 'user':
      return (p.email ?? p.uid)?.toLowerCase() ?? null;
    case 'product':
      return p.productId?.toLowerCase() ?? null;
    case 'credits':
      return p.credits ?? null;
    case 'id':
      return p.id.toLowerCase();
  }
}

// Lista global de compras (colección `processedPurchases`). Paginación
// server-side por `cursor` (el `at` en ms del último doc). Búsqueda client-side
// sobre lo cargado (email / SKU / uid / transaction id).

export function Purchases() {
  const [rows, setRows] = useState<AdminPurchase[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  async function loadPage(fromCursor: number | null, append: boolean) {
    setLoading(true);
    setErr(null);
    try {
      const res = await callListPurchases({ cursor: fromCursor ?? undefined, pageSize: 200 });
      setRows((prev) => (append ? [...prev, ...res.data.purchases] : res.data.purchases));
      setCursor(res.data.nextCursor);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadPage(null, false);
  }, []);

  const totalCredits = useMemo(() => rows.reduce((sum, p) => sum + (p.credits ?? 0), 0), [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (p) =>
        p.email?.toLowerCase().includes(q) ||
        p.productId?.toLowerCase().includes(q) ||
        p.uid.toLowerCase().includes(q) ||
        p.id.toLowerCase().includes(q),
    );
  }, [rows, query]);

  const { sorted, toggle, arrow } = useSort<AdminPurchase, SortKey>(filtered, sortValue);

  return (
    <div>
      <div className="row between" style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>
          Compras ({rows.length}) · {totalCredits} créditos
        </h2>
        <button onClick={() => loadPage(null, false)} disabled={loading}>
          {loading ? 'Cargando…' : 'Refrescar'}
        </button>
      </div>

      <div className="search-bar">
        <input
          type="search"
          placeholder="Buscar por email, SKU, UID o transaction id…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {err && <div className="error">{err}</div>}

      <div className="panel" style={{ padding: 0 }}>
        <table className="table">
          <thead>
            <tr>
              <th className="sortable" onClick={() => toggle('at')}>Fecha{arrow('at')}</th>
              <th className="sortable" onClick={() => toggle('user')}>Usuario{arrow('user')}</th>
              <th className="sortable" onClick={() => toggle('product')}>Pack (SKU){arrow('product')}</th>
              <th className="sortable num" onClick={() => toggle('credits')}>Créditos{arrow('credits')}</th>
              <th className="sortable" onClick={() => toggle('id')}>Transaction id{arrow('id')}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((p) => (
              <tr key={p.id}>
                <td className="muted">{formatDate(p.at)}</td>
                <td>
                  {p.uid ? (
                    <Link to={`/users/${p.uid}`}>{p.email || p.uid}</Link>
                  ) : (
                    '—'
                  )}
                </td>
                <td>
                  <span className="badge">{p.productId || '—'}</span>
                </td>
                <td className="num">{p.credits ?? '—'}</td>
                <td className="report-id">{p.id}</td>
              </tr>
            ))}
            {sorted.length === 0 && !loading && (
              <tr>
                <td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 32 }}>
                  Sin compras.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {cursor !== null && (
        <div className="pagination">
          <button onClick={() => loadPage(cursor, true)} disabled={loading}>
            Cargar más
          </button>
        </div>
      )}
    </div>
  );
}

function formatDate(ms: number | null): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
}
